// LYTICK UI Store
import { create } from 'zustand'
import type { ParsedDataset } from '@/lib/lytick/csv-import'

export type ModalType = 'symbolSearch' | 'replayEnd' | 'settings' | 'indicators' | null
export type DrawerType = 'tools' | null

interface UIState {
  activeModal: ModalType
  activeDrawer: DrawerType
  orientation: 'portrait' | 'landscape'
  isKeyboardVisible: boolean
  isFullscreen: boolean
  toasts: Array<{ id: string; message: string; type: 'success' | 'error' | 'info' | 'warning' }>
  symbolSearchHint: boolean
  commandPaletteOpen: boolean
  isSettingsOpen: boolean
  pendingCsvImport: { file: File; dataset: ParsedDataset } | null

  // Actions
  openModal: (modal: ModalType) => void
  closeModal: () => void
  openDrawer: (drawer: DrawerType) => void
  closeDrawer: () => void
  setOrientation: (orientation: 'portrait' | 'landscape') => void
  setKeyboardVisible: (visible: boolean) => void
  setFullscreen: (fullscreen: boolean) => void
  addToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void
  removeToast: (id: string) => void
  setSymbolSearchHint: (v: boolean) => void
  openCommandPalette: () => void
  closeCommandPalette: () => void
  openSettings: () => void
  closeSettings: () => void
  toggleSettings: () => void
  setPendingCsvImport: (value: { file: File; dataset: ParsedDataset }) => void
  clearPendingCsvImport: () => void
}

export const useUIStore = create<UIState>()((set) => ({
  activeModal: null,
  activeDrawer: null,
  orientation: 'portrait',
  isKeyboardVisible: false,
  isFullscreen: false,
  toasts: [],
  symbolSearchHint: false,
  commandPaletteOpen: false,
  isSettingsOpen: false,
  pendingCsvImport: null,

  openModal: (modal) => set({ activeModal: modal, activeDrawer: null }),
  closeModal: () => set({ activeModal: null, symbolSearchHint: false }),
  setSymbolSearchHint: (v) => set({ symbolSearchHint: v }),

  openDrawer: (drawer) => set({ activeDrawer: drawer, activeModal: null }),
  closeDrawer: () => set({ activeDrawer: null }),

  setOrientation: (orientation) => set({ orientation }),
  setKeyboardVisible: (visible) => set({ isKeyboardVisible: visible }),
  setFullscreen: (fullscreen) => set({ isFullscreen: fullscreen }),

  addToast: (message, type = 'info') => {
    const id = Date.now().toString()
    set((state) => ({
      toasts: [...state.toasts, { id, message, type }],
    }))
    // Auto-remove after 5s
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }))
    }, 5000)
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  openCommandPalette: () => set({ commandPaletteOpen: true }),
  closeCommandPalette: () => set({ commandPaletteOpen: false }),

  openSettings: () => set({ isSettingsOpen: true }),
  closeSettings: () => set({ isSettingsOpen: false }),
  toggleSettings: () => set((state) => ({ isSettingsOpen: !state.isSettingsOpen })),

  setPendingCsvImport: (value) => set({ pendingCsvImport: value }),
  clearPendingCsvImport: () => set({ pendingCsvImport: null }),
}))
